import React, { useState, useEffect } from 'react';
import { Box, List, ListItem, ListItemText, IconButton, InputAdornment, TextField, Avatar, Typography, Pagination, Skeleton, Stack, Grid, CircularProgress } from '@mui/material';
import DOMPurify from 'dompurify';
import { TabContext, TabPanel } from '@mui/lab';
import SearchIcon from '@mui/icons-material/Search';
import { TableColumn, TableData } from './type';
import { ListTableWrapper, AntSwitch } from './ListTable.styled';

interface ListTableProps {
  title: string;
  tabValue: string;
  columns: TableColumn[];
  data: TableData[];
  actions: {
    icon: React.ReactNode;
    label: string;
    color: string;
    onClick: (item: TableData) => void;
  }[];
  itemsPerPage?: number;
  onActionClick?: (action: any, item: TableData) => void;
  isStatus?: boolean;
  loading?: boolean;
}

function ListTable({ title, tabValue, columns, data, actions, itemsPerPage = 10, onActionClick, isStatus = true, loading = false }: ListTableProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const filteredData = isLoading
    ? new Array(itemsPerPage).fill(null) // Show skeletons when loading
    : data.filter((item) =>
        columns.some((column) => {
          const value = item[column.accessor];
          return value && value.toString().toLowerCase().includes(searchQuery.toLowerCase());
        }),
      );

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const visibleData = filteredData.slice(startIndex, endIndex);
  const totalRecords = filteredData.length;

  useEffect(() => {
    setCurrentPage(1); // Reset currentPage when tab changes
  }, [tabValue]);

  // Update currentPage based on the total pages available for the new tab's data
  useEffect(() => {
    const totalPagesForCurrentTab = Math.ceil(filteredData.length / itemsPerPage);
    if (currentPage > totalPagesForCurrentTab) {
      setCurrentPage(1); // Reset currentPage if it exceeds total pages for current tab's data
    }
  }, [filteredData, currentPage, itemsPerPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000); // Change the delay time as needed

    return () => clearTimeout(timer); // Clean up the timer on unmount
  }, []);

  return (
    <Grid>
      <div className="artifact-main-table">
        <ListTableWrapper>
          <div className="list_table-main-table">
            <Grid container style={{ marginBottom: 20 }} justifyContent="space-between" alignItems="center">
              <Grid item>
                <Typography variant="h4" gutterBottom>
                  {title} Performance
                </Typography>
              </Grid>
              <Grid item>
                <div className="table-row-item srch_position" style={{ border: 0 }}>
                  <Box className="actions">
                    <TextField
                      label="Search"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={searchQuery}
                      onChange={(e) => {
                        setCurrentPage(1);
                        setSearchQuery(e.target.value);
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="large">
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </div>
              </Grid>
            </Grid>
            <TabContext value={tabValue}>
              <TabPanel value={tabValue} className="override-tabpanel">
                <div className="table-row-item-cell">
                  {columns.map((column) => (
                    <Box key={column.accessor} className="list_table-table-head">
                      {column.header}
                    </Box>
                  ))}
                  {isStatus && <Box className="list_table-table-head list-table-status-head">Status</Box>}
                  {Array.isArray(actions) && <Box className="list_table-table-head list-table-action-head">Report</Box>}
                </div>
                {(() => {
                  if (isLoading) {
                    return Array.from({ length: itemsPerPage / 2 }).map((_, index) => {
                      const skeletonKey = `skeleton-${tabValue}-${Date.now()}-${Math.random()}-${index}`;
                      return (
                        <div className="table-row-item" key={skeletonKey}>
                          {columns.map((column) => (
                            <Box className="list-list_table-box-wrap" key={column.accessor}>
                              <List>
                                <ListItem className="list-list_table">
                                  <Skeleton width={300} animation="wave" variant="text" />
                                </ListItem>
                              </List>
                            </Box>
                          ))}
                          {isStatus && (
                            <Box className="list-list_table-box-wrap" key="status">
                              <List>
                                <ListItem className="list-list_table">
                                  <Skeleton width={150} animation="wave" variant="text" />
                                </ListItem>
                              </List>
                            </Box>
                          )}
                          {Array.isArray(actions) && (
                            <Box className="list-list_table-box-wrap" key="actions">
                              <List>
                                <ListItem className="list-list_table">
                                  <Skeleton width={150} animation="wave" variant="text" />
                                </ListItem>
                              </List>
                            </Box>
                          )}
                        </div>
                      );
                    });
                  }
                  if (loading) {
                    return (
                      <p style={{ textAlign: 'center', fontWeight: 400 }}>
                        <CircularProgress />
                      </p>
                    );
                  }
                  if (visibleData.length === 0) {
                    return <p style={{ textAlign: 'center', fontWeight: 400 }}>No data to display</p>;
                  }
                  return visibleData.map((item) => {
                    const uniqueKey = item?.id || item?.user_id || item?.employeeID || item?.profile?.user_id || `${tabValue}-${item.email || JSON.stringify(item)}`;
                    return (
                      <div className="table-row-item" key={uniqueKey}>
                        {columns.map((column) => {
                          const renderColumnContent = () => {
                            if (column.type === 'image' || column.type === 'Image') {
                              return <Avatar alt="Image" src={decodeURIComponent(item[column.accessor])} />;
                            }
                            if (column.type === 'html' || column.type === 'HTML') {
                              return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item[column.accessor]) }} />;
                            }
                            const renderPrimary = () => {
                              if (column.accessor === 'business unit') {
                                return <span title={item.profile?.department}>{item.profile?.department}</span>;
                              }
                              if (item.email === item[column.accessor]) {
                                return <span title={item[column.accessor]}>{item[column.accessor]}</span>;
                              }
                              return item[column.accessor];
                            };
                            return <ListItemText className="listIem-text" primary={renderPrimary()} />;
                          };
                          return (
                            <Box className="list-list_table-box-wrap" key={column.accessor}>
                              <List>
                                <ListItem className="list-list_table">{renderColumnContent()}</ListItem>
                              </List>
                            </Box>
                          );
                        })}
                        {isStatus && (
                          <Box className="status-field-wrap" m={2}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography>Inactive</Typography>
                              <AntSwitch
                                defaultChecked={item.status === 1}
                                inputProps={{ 'aria-label': 'ant design' }}
                                onClick={() => {
                                  if (onActionClick) {
                                    onActionClick({ label: 'status' }, item);
                                  }
                                }}
                              />
                              <Typography>Active</Typography>
                            </Stack>
                          </Box>
                        )}
                        <Box className="actions-field-wrap">
                          {actions.map((action) => {
                            const userId = item?.user_id || item?.profile?.user_id || item?.employeeID || item?.id;
                            const isViewAction = action.label === 'View';
                            const link = isViewAction && userId ? `/user-artifact-detail/${userId}` : undefined;
                            const actionKey = `${uniqueKey}-${action.label}`;

                            return (
                              <span key={actionKey}>
                                {isViewAction && link ? (
                                  <a href={link} target="_self" className="primary-icon">
                                    <IconButton size="large" sx={{ color: '#2559C3' }}>
                                      {action.icon}
                                    </IconButton>
                                  </a>
                                ) : (
                                  <IconButton
                                    size="large"
                                    sx={{ color: '#2559C3' }}
                                    className="primary-icon"
                                    onClick={() => {
                                      if (onActionClick) {
                                        onActionClick(action, item);
                                      }
                                    }}
                                  >
                                    {action.icon}
                                  </IconButton>
                                )}
                              </span>
                            );
                          })}
                        </Box>
                      </div>
                    );
                  });
                })()}
              </TabPanel>
            </TabContext>
          </div>
          <Box mt={2} mr={2} display="flex" justifyContent="flex-end">
            <div className="pgntn_box">
              <Typography className="showing_rslt" variant="body2" color="textSecondary">
                Showing {visibleData.length} records out of {totalRecords} total.
              </Typography>
              <Pagination count={totalPages} page={currentPage} onChange={(event, page) => setCurrentPage(page)} color="primary" size="large" className="pagination cs_pgntn" siblingCount={1} boundaryCount={1} showFirstButton showLastButton />
            </div>
          </Box>
        </ListTableWrapper>
      </div>
    </Grid>
  );
}

ListTable.defaultProps = {
  itemsPerPage: 10,
  onActionClick: undefined,
  isStatus: true,
  loading: false,
};

export default ListTable;
